import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js'
import type { TransactionError } from '@solana/web3.js'
import { DynamicBondingCurveClient } from '@meteora-ag/dynamic-bonding-curve-sdk'
import { captureError, addBreadcrumb } from '@/lib/sentry'

// Phase 1 of RPC migration: Helius removed. Triton primary, Alchemy fallback.
// Helius is webhooks-only (admin has no webhook endpoints, so no Helius
// references in this repo at all after this commit).
const TRITON_URL = (import.meta.env.VITE_TRITON_RPC_URL || '').trim()
const ALCHEMY_URL = (import.meta.env.VITE_ALCHEMY_RPC_URL || '').trim()
const RPC_URL_RAW = TRITON_URL || ALCHEMY_URL

const isDev = import.meta.env.DEV
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const isProdHost =
  hostname.endsWith('dextoro.com') || hostname.endsWith('.pages.dev')

export const IS_RPC_CONFIGURED = Boolean(RPC_URL_RAW)

let RPC_URL: string
if (RPC_URL_RAW) {
  RPC_URL = RPC_URL_RAW
} else if (isDev && !isProdHost) {
  console.warn(
    '[solana] VITE_TRITON_RPC_URL not configured, on-chain features disabled in dev. Set it in .env.local to enable.'
  )
  RPC_URL = 'http://127.0.0.1:0'
} else {
  throw new Error('Solana RPC URL is required in production')
}

export const RPC_ENDPOINT = RPC_URL

export const connection = new Connection(RPC_URL, 'confirmed')

// Alchemy fallback connection. Only constructed when both Triton and
// Alchemy are configured, so dev with only Alchemy set keeps `connection`
// as Alchemy and disables the fallback layer (no double-routing to itself).
const fallbackConnection: Connection | null =
  TRITON_URL && ALCHEMY_URL ? new Connection(ALCHEMY_URL, 'confirmed') : null

/**
 * Try the primary (Triton) connection, fall back to Alchemy on any error.
 * Read-only ops only — write paths (sendRawTransaction in Treasury.tsx)
 * stay on the primary connection. Sentry breadcrumb on every fallback
 * uses the locked vocab: source, target, failover_reason, fn. Matches
 * the Phase 0 proxy + launch File 1-5 tag shape so dashboards aggregate
 * cleanly. Exported so Phase 2 callers (dammV2.ts, Treasury.tsx reads)
 * can opt in without a separate helper.
 *
 * If only one of the two providers is configured, this becomes a
 * single-attempt call against whichever is set. Errors propagate normally.
 */
export async function withRpcFallback<T>(
  label: string,
  fn: (conn: Connection) => Promise<T>
): Promise<T> {
  try {
    return await fn(connection)
  } catch (err) {
    if (!fallbackConnection) throw err
    const reason = err instanceof Error ? err.message : String(err)
    console.warn(`[solana] Triton failed on ${label}, retrying via Alchemy: ${reason}`)
    addBreadcrumb(`fallback: ${label}`, 'admin_rpc', {
      source: 'admin-solana-lib',
      target: 'alchemy',
      failover_reason: reason,
      fn: label,
    })
    return fn(fallbackConnection)
  }
}

const CONFIRMATION_TIMEOUT_MS = 90_000
const CONFIRMATION_POLL_INTERVAL_MS = 1_500

/**
 * Wait for a tx signature to reach 'confirmed' status with explicit timeout
 * + RPC fallback. Replaces raw connection.confirmTransaction(), which has
 * neither: those calls hang forever on stalled RPC even when the tx has
 * already landed on-chain (the admin Treasury "Confirming..." stuck state
 * surfaced 2026-05-05 after the Pack 1 CSP fix unblocked broadcasts).
 *
 * Polls getSignatureStatuses via the existing withRpcFallback chain so a
 * slow Triton response falls through to Alchemy. Returns the same
 * { value: { err } } shape as connection.confirmTransaction so call sites
 * only swap the function name.
 *
 * Throws on timeout (90s, blockhash expiry plus slack), which forces the
 * caller's catch block to run. The caller sets claimState to 'error' and
 * unsticks the UI; the user can verify on Solscan whether the tx landed.
 */
export async function confirmTransactionWithFallback(
  signature: string,
): Promise<{ value: { err: TransactionError | null } }> {
  const deadline = Date.now() + CONFIRMATION_TIMEOUT_MS
  while (Date.now() < deadline) {
    try {
      const result = await withRpcFallback(
        'confirmTransactionWithFallback',
        (c) => c.getSignatureStatuses([signature]),
      )
      const status = result.value[0]
      if (status?.err) {
        return { value: { err: status.err } }
      }
      if (
        status?.confirmationStatus === 'confirmed' ||
        status?.confirmationStatus === 'finalized'
      ) {
        return { value: { err: null } }
      }
    } catch (err) {
      // Transient poll errors don't terminate the wait. withRpcFallback
      // already tried Alchemy; keep polling until the deadline.
      console.warn(
        '[solana] confirmation poll error:',
        err instanceof Error ? err.message : err,
      )
    }
    await new Promise((r) => setTimeout(r, CONFIRMATION_POLL_INTERVAL_MS))
  }
  throw new Error('Transaction confirmation timeout: check Solscan for status')
}

// dextoro Treasury Address (feeClaimer for all Meteora DBC pools)
export const TREASURY_ADDRESS = new PublicKey(
  '9ynRciWU7m3vGtGmm6s9YRW2avXA5D64AMbcedhakxQL'
)

// Meteora Dynamic Bonding Curve Program
export const METEORA_DBC_PROGRAM = new PublicKey(
  'dbcij3LWUppWqq96dh6gJWwBifmcGfLSB5D4DuSMaqN'
)

// Meteora DAMM V2 Program (post-graduation pools)
export const DAMM_V2_PROGRAM = new PublicKey(
  'cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG'
)

// IMPORTANT: must match the active DBC config in
// dextoro-launch/src/lib/meteora/dbcClient.ts (DEXTORO_DBC_CONFIG_KEY).
// Active: DBC v5 (CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao), 200bps trading fee, 2026-05-06.
// On config rotation, this MUST be updated together with all other duplicates.
export const DBC_CONFIG_KEY = new PublicKey(
  'CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao'
)

// DTR Staking Rewards Vault
export const REWARDS_VAULT_ADDRESS = new PublicKey(
  'FLjE4YNBAhztKxiTYdN24GF1Ttd9yhzdV1AV3CuEf6VZ'
)

// DTR Staking Program
export const STAKING_PROGRAM_ID = new PublicKey(
  'HejqGag9q1mqkYNe3iU9tMs4S1s9g3fegdMBvicMcF4M'
)

// GlobalState PDA for the staking program
export const GLOBAL_STATE_PDA = new PublicKey(
  'D7GWaNkyhpMAZHCEdKUyeaSsCUVBpRjmYAbwuUQwaY8y'
)

// Minimum claimable fee threshold (dust filter, matching launch app)
export const MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS = 10_000

const DTR_DECIMALS = 6
const SOL_DECIMALS = 9

export interface GlobalStateData {
  totalStaked: number
  epochCount: number
  totalRewardsDistributed: number
  admin: string
}

/**
 * Get the current SOL balance of the treasury wallet
 */
export async function getTreasuryBalance(): Promise<number> {
  if (!IS_RPC_CONFIGURED) return 0
  try {
    const balance = await withRpcFallback('getTreasuryBalance', (c) => c.getBalance(TREASURY_ADDRESS))
    return balance / LAMPORTS_PER_SOL
  } catch (error) {
    console.error('Failed to fetch treasury balance:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch treasury balance'))
    return 0
  }
}

/**
 * Get the current SOL balance of the rewards vault
 */
export async function getRewardsVaultBalance(): Promise<number> {
  if (!IS_RPC_CONFIGURED) return 0
  try {
    const balance = await withRpcFallback('getRewardsVaultBalance', (c) => c.getBalance(REWARDS_VAULT_ADDRESS))
    return balance / LAMPORTS_PER_SOL
  } catch (error) {
    console.error('Failed to fetch rewards vault balance:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch rewards vault balance'))
    return 0
  }
}

/**
 * Read the GlobalState PDA account and deserialize staking metrics.
 * Layout (Anchor/Borsh):
 *   0-7:     discriminator (8 bytes)
 *   8-39:    admin pubkey (32)
 *   40-71:   dtrMint pubkey (32)
 *   72-103:  dtrVault pubkey (32)
 *   104:     rewardsVaultBump (1)
 *   105-112: totalStaked u64 LE (8)
 *   113-128: globalRewardPerToken u128 (16)
 *   129-136: totalRewardsDistributed u64 LE (8)
 *   137-140: epochCount u32 LE (4)
 *   141:     bump (1)
 */
export async function fetchGlobalState(): Promise<GlobalStateData | null> {
  if (!IS_RPC_CONFIGURED) return null
  try {
    const accountInfo = await withRpcFallback('fetchGlobalState', (c) => c.getAccountInfo(GLOBAL_STATE_PDA))
    if (!accountInfo?.data) return null

    const data = accountInfo.data

    // admin: pubkey at offset 8 (32 bytes). Used by Infrastructure dashboard
    // to display the staking admin wallet identity. Existing callers ignore
    // this field; safe to add.
    const admin = new PublicKey(data.slice(8, 40)).toBase58()

    // totalStaked: u64 LE at offset 105
    const totalStakedRaw = data.readBigUInt64LE(105)
    const totalStaked = Number(totalStakedRaw) / 10 ** DTR_DECIMALS

    // totalRewardsDistributed: u64 LE at offset 129
    const totalRewardsDistributedRaw = data.readBigUInt64LE(129)
    const totalRewardsDistributed = Number(totalRewardsDistributedRaw) / 10 ** SOL_DECIMALS

    // epochCount: u32 LE at offset 137
    const epochCount = data.readUInt32LE(137)

    return { totalStaked, epochCount, totalRewardsDistributed, admin }
  } catch (error) {
    console.error('Failed to fetch GlobalState:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch GlobalState'))
    return null
  }
}

// ────────────────────────────────────────────────────────────────────────
// Infrastructure dashboard helpers (added 2026-04-25 for /infrastructure)
// ────────────────────────────────────────────────────────────────────────

export interface SolanaEpochInfo {
  epoch: number
  slotIndex: number
  slotsInEpoch: number
  absoluteSlot: number
  blockHeight: number | null
}

/**
 * Get current Solana epoch progress. Returns null on RPC failure.
 */
export async function getEpochInfo(): Promise<SolanaEpochInfo | null> {
  if (!IS_RPC_CONFIGURED) return null
  try {
    const info = await withRpcFallback('getEpochInfo', (c) => c.getEpochInfo())
    return {
      epoch: info.epoch,
      slotIndex: info.slotIndex,
      slotsInEpoch: info.slotsInEpoch,
      absoluteSlot: info.absoluteSlot,
      blockHeight: info.blockHeight ?? null,
    }
  } catch (error) {
    console.error('Failed to fetch epoch info:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch epoch info'))
    return null
  }
}

export interface PriorityFeeStats {
  p50: number
  p95: number
  sampleSize: number
}

/**
 * Get p50 and p95 of recent priority fees (microLamports per CU).
 * RPC returns up to 150 most recent slots' priority fees.
 */
export async function getRecentPriorityFeeStats(): Promise<PriorityFeeStats | null> {
  if (!IS_RPC_CONFIGURED) return null
  try {
    const fees = await withRpcFallback('getRecentPriorityFeeStats', (c) => c.getRecentPrioritizationFees())
    if (!fees.length) return { p50: 0, p95: 0, sampleSize: 0 }
    const sorted = fees.map(f => f.prioritizationFee).sort((a, b) => a - b)
    const p50Index = Math.floor(sorted.length * 0.5)
    const p95Index = Math.floor(sorted.length * 0.95)
    return {
      p50: sorted[p50Index] ?? 0,
      p95: sorted[Math.min(p95Index, sorted.length - 1)] ?? 0,
      sampleSize: sorted.length,
    }
  } catch (error) {
    console.error('Failed to fetch recent priority fees:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch priority fees'))
    return null
  }
}

export interface DbcFeeClaimerCheck {
  matches: boolean
  actual: string | null
  checkedAt: number
}

// Singleton DBC client (avoids re-instantiating on every call)
let dbcClient: DynamicBondingCurveClient | null = null
function getDbcClient(): DynamicBondingCurveClient {
  if (!dbcClient) dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')
  return dbcClient
}

/**
 * Read the dextoro DBC config account on-chain and verify its feeClaimer field
 * still points to the treasury. Fail-closed (returns matches:false) on any
 * error so the dashboard surfaces the mismatch and ops investigates.
 *
 * Mirrors the inline check from
 * dextoro-launch/src/lib/meteora/feeMetricsClient.ts:185-191.
 */
export async function validateDbcFeeClaimer(): Promise<DbcFeeClaimerCheck> {
  if (!IS_RPC_CONFIGURED) {
    return { matches: false, actual: null, checkedAt: Date.now() }
  }
  try {
    const config = await getDbcClient().state.getPoolConfig(DBC_CONFIG_KEY)
    const actual = config?.feeClaimer?.toBase58() ?? null
    const expected = TREASURY_ADDRESS.toBase58()
    return { matches: actual === expected, actual, checkedAt: Date.now() }
  } catch (error) {
    console.error('Failed to validate DBC feeClaimer:', error)
    captureError(error instanceof Error ? error : new Error('Failed to validate DBC feeClaimer'))
    return { matches: false, actual: null, checkedAt: Date.now() }
  }
}

export interface StakingAdminInfo {
  admin: string | null
  balanceSol: number
}

/**
 * Read the staking program's GlobalState.admin field, then fetch that wallet's
 * SOL balance. Returns admin:null if GlobalState read fails.
 */
export async function getStakingAdminInfo(): Promise<StakingAdminInfo> {
  if (!IS_RPC_CONFIGURED) return { admin: null, balanceSol: 0 }
  try {
    const globalState = await fetchGlobalState()
    if (!globalState?.admin) return { admin: null, balanceSol: 0 }
    const balanceLamports = await withRpcFallback(
      'getStakingAdminInfo.getBalance',
      (c) => c.getBalance(new PublicKey(globalState.admin))
    )
    return {
      admin: globalState.admin,
      balanceSol: balanceLamports / LAMPORTS_PER_SOL,
    }
  } catch (error) {
    console.error('Failed to fetch staking admin info:', error)
    captureError(error instanceof Error ? error : new Error('Failed to fetch staking admin info'))
    return { admin: null, balanceSol: 0 }
  }
}

/**
 * Read partnerQuoteFee (u64 LE) at byte offset 272 from raw Meteora DBC
 * VirtualPool account data. Returns fee in lamports, or 0 on failure.
 * Offset 272 = partnerQuoteFee per Meteora DBC VirtualPool layout
 * (documented in sync-pool-stats edge function).
 */
export function readPartnerQuoteFee(data: Buffer | Uint8Array): number {
  if (data.length < 280) return 0
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const lo = view.getUint32(272, true)
  const hi = view.getUint32(276, true)
  return hi * 0x100000000 + lo
}

export interface OnChainClaimableFees {
  totalLamports: number
  claimablePoolCount: number
}

/**
 * Per-pool fee data parsed from raw on-chain account bytes.
 */
export interface PoolFeeData {
  poolAddress: string
  partnerQuoteFeeLamports: number
  isMigrated: boolean
  migrationFeeWithdrawStatus: number // bitfield: bit 1 (0b010) = creator, bit 2 (0b100) = partner
}

/**
 * Read isMigrated (u8) from VirtualPool account data.
 * Offset: partnerQuoteFee(272) + 8 + sqrtPrice(16) + activationPoint(8) + poolType(1) = 305
 */
function readIsMigrated(data: Buffer | Uint8Array): boolean {
  if (data.length < 306) return false
  return data[305] === 1
}

/**
 * Read migrationFeeWithdrawStatus (u8) from VirtualPool account data.
 * Offset: isMigrated(305) + 1 + isPartnerWithdrawSurplus(1) + isProtocolWithdrawSurplus(1) +
 *   migrationProgress(1) + isWithdrawLeftover(1) + isCreatorWithdrawSurplus(1) = 311
 */
function readMigrationFeeWithdrawStatus(data: Buffer | Uint8Array): number {
  if (data.length < 312) return 0
  return data[311]
}

/**
 * Batch fetch pool accounts via getMultipleAccountsInfo (100 per call),
 * parse partnerQuoteFee from each, apply dust filter, return totals.
 */
export async function fetchOnChainClaimableFees(
  poolAddresses: string[]
): Promise<OnChainClaimableFees> {
  if (!IS_RPC_CONFIGURED) return { totalLamports: 0, claimablePoolCount: 0 }
  let totalLamports = 0
  let claimablePoolCount = 0
  const BATCH_SIZE = 100

  for (let i = 0; i < poolAddresses.length; i += BATCH_SIZE) {
    const batch = poolAddresses.slice(i, i + BATCH_SIZE)
    const pubkeys = batch.map((addr) => new PublicKey(addr))

    try {
      const accounts = await withRpcFallback('fetchOnChainClaimableFees', (c) => c.getMultipleAccountsInfo(pubkeys))
      for (const account of accounts) {
        if (!account?.data) continue
        const fee = readPartnerQuoteFee(account.data)
        if (fee >= MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS) {
          totalLamports += fee
          claimablePoolCount++
        }
      }
    } catch (error) {
      console.error(`[solana] Failed to fetch pool batch at index ${i}:`, error)
      captureError(
        error instanceof Error ? error : new Error('Failed to fetch pool batch'),
        { batchIndex: i, batchSize: batch.length }
      )
    }
  }

  return { totalLamports, claimablePoolCount }
}

/**
 * Batch fetch per-pool fee data including migration status.
 * Returns one entry per pool with partnerQuoteFee, isMigrated, and migrationFeeWithdrawStatus.
 */
export async function fetchPoolFeeDetails(
  poolAddresses: string[]
): Promise<PoolFeeData[]> {
  if (!IS_RPC_CONFIGURED) return []
  const results: PoolFeeData[] = []
  const BATCH_SIZE = 100

  for (let i = 0; i < poolAddresses.length; i += BATCH_SIZE) {
    const batch = poolAddresses.slice(i, i + BATCH_SIZE)
    const pubkeys = batch.map((addr) => new PublicKey(addr))

    try {
      const accounts = await withRpcFallback('fetchPoolFeeDetails', (c) => c.getMultipleAccountsInfo(pubkeys))
      for (let j = 0; j < accounts.length; j++) {
        const account = accounts[j]
        if (!account?.data) continue

        results.push({
          poolAddress: batch[j],
          partnerQuoteFeeLamports: readPartnerQuoteFee(account.data),
          isMigrated: readIsMigrated(account.data),
          migrationFeeWithdrawStatus: readMigrationFeeWithdrawStatus(account.data),
        })
      }
    } catch (error) {
      console.error(`[solana] Failed to fetch pool details batch at index ${i}:`, error)
      captureError(
        error instanceof Error ? error : new Error('Failed to fetch pool details batch'),
        { batchIndex: i, batchSize: batch.length }
      )
    }
  }

  return results
}

/**
 * Count unique stakers by reading all UserStake accounts from the staking program.
 * UserStake account size = 81 bytes (8 discriminator + 32 owner + 8 staked + 16 rewardDebt + 8 totalClaimed + 8 lastStakeTimestamp + 1 bump).
 * Returns null if the RPC endpoint does not support getProgramAccounts.
 */
export async function fetchStakerCount(): Promise<number | null> {
  if (!IS_RPC_CONFIGURED) return null
  try {
    const accounts = await withRpcFallback('fetchStakerCount', (c) =>
      c.getProgramAccounts(STAKING_PROGRAM_ID, {
        dataSlice: { offset: 40, length: 8 }, // staked field: u64 at offset 40
        filters: [{ dataSize: 81 }],
      })
    )

    let count = 0
    for (const { account } of accounts) {
      const staked = account.data.readBigUInt64LE(0)
      if (staked > 0n) count++
    }
    return count
  } catch (error) {
    console.error('Failed to fetch staker count (getProgramAccounts may not be supported):', error)
    captureError(
      error instanceof Error ? error : new Error('Failed to fetch staker count'),
      { note: 'getProgramAccounts may not be supported' }
    )
    return null
  }
}
